import { checkboxAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/styled-system';
import { Borders, Colors, FontSizes, Radii, Shadows, Space, BorderWidths, TextStyles } from '../../types';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: Space.XS,
  },
  container: {
    transitionProperty: 'common',
    transitionDuration: 'normal',
    display: 'flex',
    alignItems: 'start',
    gap: Space.XS,
    _hover: {
      control: {
        borderColor: Colors.BORDER_EMPHASIS,
        bg: Colors.SURFACE_PAGE,
      },
    },
  },
  label: {
    color: Colors.TEXT_MUTED,
    '&': {
      apply: `textStyles.${TextStyles.UI_DEFAULT}`,
    },
    m: 0,
    align: 'baseline',
    _checked: {
      color: Colors.TEXT_EMPHASIS,
    },
  },
  control: {
    boxSize: '1.125em',
    lineHeight: 'inherit',
    my: 'px',
    border: '1px solid',
    fontSize: 'inherit',
    borderRadius: Radii.SM,
    borderWidth: BorderWidths.XS,
    borderColor: Colors.BORDER_INPUT,
    background: Colors.SURFACE_PAGE,
    svg: {
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      boxSize: '1em',
      color: Colors.TEXT_ON_PRIMARY,
    },
    _hover: {
      borderColor: Colors.BORDER_EMPHASIS,
    },
    _indeterminate: {
      background: Colors.SURFACE_PRIMARY,
      borderColor: Colors.SURFACE_PRIMARY_ACTIVE,
    },
    _checked: {
      background: Colors.SURFACE_BRAND,
      borderColor: Colors.BORDER_SUBDUED,
      color: Colors.TEXT_ON_PRIMARY,
      _hover: {
        bg: Colors.SURFACE_PRIMARY_HOVER,
        borderColor: Colors.BORDER_SUBDUED,
      },
      _disabled: {
        bg: Colors.SURFACE_DISABLED,
        color: Colors.TEXT_DISABLED,
      },
      _invalid: {
        bg: Colors.SURFACE_ERROR,
        borderColor: Colors.BORDER_ERROR,
      },
    },
    _disabled: {
      bg: Colors.SURFACE_DISABLED,
      color: Colors.TEXT_DISABLED,
    },
    _invalid: {
      bg: Colors.SURFACE_ERROR_SUBDUED,
      borderColor: Colors.BORDER_ERROR,
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: Colors.BORDER_FOCUS,
      outlineOffset: '1px',
      boxShadow: 'none',
    },
  },
});

const variantrStacked = definePartsStyle({
  control: {
    justifySelf: 'center',
    margin: '0 auto',
  },
  label: {
    w: 'auto',
    textAlign: 'center',
  },
  container: {
    flexDirection: 'column',
    display: 'inline-flex',
    alignItems: 'center',
    flex: '1',
  },
});

const variantCard = definePartsStyle({
  container: defineStyle({
    py: Space.XS,
    px: Space.SM,
    pe: Space.XS,
    bg: Colors.SURFACE_PAGE,
    border: Borders['1PX'],
    borderColor: Colors.BORDER_DEFAULT,
    rounded: Radii.LG,
    position: 'relative',
    me: Space.XS,
    gap: Space.XS,
    w: 'full',
    h: 'full',
    _hover: {
      borderColor: Colors.BORDER_INPUT,
      bg: Colors.SURFACE_HOVER,
    },
    _checked: {
      bg: Colors.SURFACE_SELECTED,
      borderColor: Colors.BORDER_BRAND,
    },
    '&:has(input:focus-visible)': {
      outline: '2px solid',
      outlineColor: Colors.BORDER_FOCUS,
      outlineOffset: '1px',
      boxShadow: 'none',
    },
  }),
  label: defineStyle({
    flex: '1',
    alignSelf: 'center',
  }),
  control: defineStyle({
    order: '2',
    _focus: {
      boxShadow: Shadows.NONE,
      outline: 'none',
    },
  }),
});

const variants = {
  card: variantCard,
  stacked: variantrStacked,
};

const sizes = {
  lg: {
    control: {
      boxSize: 6,
    },
    label: {
      '& *': {
        apply: `textStyles.${TextStyles.UI_DEFAULT}`,
        fontWeight: 'normal',
      },
    },
  },
  md: {
    control: {
      boxSize: 4,
      mt: 0.5,
    },
    label: {
      '& *': {
        apply: `textStyles.${TextStyles.UI_DEFAULT}`,
        fontWeight: 'normal',
      },
    },
  },
  sm: {
    control: {
      boxSize: 4,
    },
    label: {
      fontSize: FontSizes.SM,
    },
  },
  xs: {
    control: {
      boxSize: 3.5,
    },
    label: {
      fontSize: FontSizes.XS,
    },
  },
};

export const Checkbox = defineMultiStyleConfig({
  baseStyle,
  variants,
  sizes,
  defaultProps: {
    size: 'md',
    colorScheme: 'brand',
  },
});
